import clsx from 'clsx';
import { ReactNode } from 'react';

import styles from './tabs.module.scss';

export type TabItem<TValue extends string> = {
  value: TValue;
  label: ReactNode;
  disabled?: boolean;
  'data-testid'?: string;
};

type Props<TValue extends string> = {
  items: TabItem<TValue>[];
  active: TValue;
  onChange: ( value: TValue ) => void;
  variant?: 'folder' | 'plain';
  className?: string;
  'data-testid'?: string;
};

/**
 * Two visual styles, same controlled API:
 *  - 'folder' (default): rounded-top active tab that breaks the underline
 *    inside its own width (Figma 40002692:9893).
 *  - 'plain': flat text buttons; the active tab gets a blue underline. Used
 *    where the page already has a card surrounding the tab content and a
 *    folder card-on-card look would compete.
 */
export function Tabs<TValue extends string>( {
  items,
  active,
  onChange,
  variant = 'folder',
  className,
  'data-testid': dataTestId
}: Props<TValue> ) {
  const containerClass = variant === 'plain' ? styles.tabsPlain : styles.tabs;
  const tabClass = variant === 'plain' ? styles.tabPlain : styles.tab;
  const activeClass = variant === 'plain' ? styles.tabPlainActive : styles.tabActive;

  return (
    <div
      className={ clsx( containerClass, className ) }
      role="tablist"
      data-testid={ dataTestId }
    >
      { items.map( ( item ) => {
        const isActive = item.value === active;
        return (
          <button
            key={ item.value }
            type="button"
            role="tab"
            aria-selected={ isActive }
            disabled={ item.disabled }
            className={ clsx( tabClass, isActive && activeClass ) }
            onClick={ () => onChange( item.value ) }
            data-testid={ item['data-testid'] }
          >
            { item.label }
          </button>
        );
      } ) }
    </div>
  );
}
